import { LocaleData, getLocaleData } from './i18n';

declare global {
  interface Window {
    __MB_INTL_PROVIDER_ACTIVE__?: boolean;
    __MB_LOCALE_DATA__?: LocaleData;
  }
}

// Global promise to ensure getLocaleData is only called once
let localeDataPromise: Promise<LocaleData> | null = null;

// Initialize global state
if (typeof window !== 'undefined') {
  window.__MB_INTL_PROVIDER_ACTIVE__ =
    window.__MB_INTL_PROVIDER_ACTIVE__ ?? false;
  window.__MB_LOCALE_DATA__ = window.__MB_LOCALE_DATA__ ?? undefined;
}

export type IntlProviderDataResult = {
  shouldRenderProvider: boolean;
  localeData: LocaleData;
  isLoading: boolean;
};

export const useIntlProviderData = (): Promise<IntlProviderDataResult> => {
  return new Promise((resolve) => {
    // Check if another IntlProvider is already active
    const isActive = window.__MB_INTL_PROVIDER_ACTIVE__;
    const existingData = window.__MB_LOCALE_DATA__;

    if (isActive && existingData) {
      // Another provider is active, use existing data
      resolve({
        shouldRenderProvider: false,
        localeData: existingData,
        isLoading: false,
      });
    } else {
      window.__MB_INTL_PROVIDER_ACTIVE__ = true;

      // Create or reuse the shared promise to ensure getLocaleData is only called once
      if (!localeDataPromise) {
        localeDataPromise = getLocaleData()
          .then((data) => {
            // Store globally for other apps
            window.__MB_LOCALE_DATA__ = data;

            return data;
          })
          .catch(() => {
            // Fallback on error
            const fallbackData: LocaleData = {
              currentLocale: 'en',
              messages: {},
            };
            window.__MB_LOCALE_DATA__ = fallbackData;
            return fallbackData;
          });
      }

      // Use the shared promise
      localeDataPromise.then((data) => {
        resolve({
          shouldRenderProvider: true,
          localeData: data,
          isLoading: false,
        });
      });
    }
  });
};
